import React, { useState, useRef, useEffect } from 'react';
import { Printer, Download, ArrowLeft, Plus, Trash2, RefreshCw } from 'lucide-react';

const PurchaseOrderPrint = () => {
  const printRef = useRef();
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({});

  // Company data
  const companyData = {
    name: "Nissi Infotech",
    tagline: "CIVIL CONSTRUCTION",
    address: "3/42-12, Nagappa colony, kavundampalayam, Salem-636030, Tamil Nadu, India",
    phone: "Phone No. 9003744644",
    email: "infraconsoft-implementation@nissiinfotech.in"
  };

  // Fetch data from API
  const fetchPurchaseOrderData = async (queryParams) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query string from parameters
      const apiParams = new URLSearchParams({
        branchtype: queryParams.branchtype || '',
        radiochecked: queryParams.radiochecked || '',
        branchId: queryParams.branchId || '22',
        poNo: queryParams.poNo || '',
        fromDate: queryParams.fromDate || '25/04/2022',
        toDate: queryParams.toDate || '19/08/2025',
        supplierId: queryParams.supplierId || '',
        projectId: queryParams.projectId || '',
        status: queryParams.status || 'W',
        storeId: queryParams.storeId || '2',
        pur_type: queryParams.pur_type || 'OP'
      }).toString();

      const apiUrl = `https://www.nissi-india.com/api/getPurchaseOrderReport?${apiParams}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("data",data)
      
      // Transform API data to match component structure
      const transformedData = Array.isArray(data.data) ? data.data.map((item, index) => ({
        id: item.id || index + 1,
        sno: index + 1,
        purReqNo: item.fld_PurchaseRequestNo|| item.pur_req_no || '-',
        qtnNo: item.fld_quotationno || item.qtn_no || '-',
        purOrderNo: item.fld_POno || item.pur_order_no || item.poNo || '-',
        date: item.fld_date|| item.order_date || '-',
        supplier: item.fld_suppliername || item.supplier_name || '-',
        dueDate: item.fld_duedate || item.due_date || '-',
        store: item.fld_storename || item.store_name || '-',
        amount: item.fld_totalNew  || '₹0.00',
        status: item.Status || 'Pending'
      })) : [];
      
      setOrders(transformedData);
    } catch (err) {
      console.error('Error fetching purchase order data:', err);
      setError(`Failed to fetch data: ${err.message}`);
      // Set sample data on error for demonstration
      setOrders([
        {
          id: 1,
          sno: 1,
          purReqNo: "PR-2022-001",
          qtnNo: "QTN-001",
          purOrderNo: "PO-2022-001",
          date: "05/05/2022",
          supplier: "ABC Suppliers Ltd",
          dueDate: "15/05/2022",
          store: "Main Store",
          amount: "₹25,000.00",
          status: "Pending"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Extract URL parameters and fetch data on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const extractedParams = {
      supplierId: urlParams.get('supplierId') || '',
      branchId: urlParams.get('branchId') || '',
      poNo: urlParams.get('poNo') || '',
      fromDate: urlParams.get('fromDate') || '',
      toDate: urlParams.get('toDate') || '',
      projectId: urlParams.get('projectId') || '',
      status: urlParams.get('status') || '',
      storeId: urlParams.get('storeId') || '',
      radiochecked: urlParams.get('radiochecked') || '',
      branchtype: urlParams.get('branchtype') || '',
      pur_type: urlParams.get('pur_type') || ''
    };
    
    setParams(extractedParams);
    fetchPurchaseOrderData(extractedParams);
  }, []);

  const calculateTotal = () => {
    return orders.reduce((total, order) => {
      const amount = parseFloat(order.amount.replace(/[₹,]/g, ''));
      return total + amount;
    }, 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR'
    });
  };

  const addNewRow = () => {
    const newOrder = {
      id: Date.now(),
      sno: orders.length + 1,
      purReqNo: "",
      qtnNo: "",
      purOrderNo: "",
      date: "",
      supplier: "",
      dueDate: "",
      store: "",
      amount: "₹0.00",
      status: "Pending"
    };
    setOrders([...orders, newOrder]);
  };

  const removeRow = (id) => {
    const updatedOrders = orders.filter(order => order.id !== id);
    // Re-number the remaining orders
    const reNumberedOrders = updatedOrders.map((order, index) => ({
      ...order,
      sno: index + 1
    }));
    setOrders(reNumberedOrders);
  };

  const updateOrder = (id, field, value) => {
    setOrders(orders.map(order => 
      order.id === id ? { ...order, [field]: value } : order
    ));
  };

  const handleRefresh = () => {
    fetchPurchaseOrderData(params);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log("Download functionality would be implemented here");
  };

  const handleBack = () => {
    console.log("Navigate back");
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Header Controls - Hidden in print */}
      <div className="print:hidden bg-gray-50 shadow-sm border-b p-4 no-print">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              
              {/* API Status Indicator */}
              <div className="flex items-center gap-2 text-sm">
                {loading && <span className="text-blue-600">Loading...</span>}
                {error && <span className="text-red-600">API Error</span>}
                {!loading && !error && (
                  <span className="text-green-600">
                    Loaded {orders.length} records
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-sm transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              
              <button
                onClick={addNewRow}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded text-sm transition-colors"
              >
                <Plus size={16} />
                Add Row
              </button>
              
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm transition-colors"
              >
                {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
              </button>
              
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded text-sm transition-colors"
              >
                <Download size={16} />
                PDF
              </button>
              
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-white hover:bg-gray-900 rounded text-sm transition-colors"
              >
                <Printer size={16} />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Content */}
      <div className="max-w-4xl mx-auto print:max-w-full print:mx-0 p-4 print:p-0">
        <div
          ref={printRef}
          className="bg-white print:bg-white border-2 border-gray-800 print:border-2 print:border-black"
        >
          {/* Company Header - Traditional Receipt Style */}
          <div className="border-b-2 border-gray-800 print:border-b-2 print:border-black p-4">
            <div className="flex items-center gap-4">
              {/* Company Logo Placeholder */}
              <div className="w-16 h-16 bg-black print:bg-black border border-gray-400 print:border-black flex items-center justify-center">
                <div className="text-white text-xs font-bold text-center">
                  <div>NISSI</div>
                  <div>INFOTECH</div>
                  <div>PVT LTD</div>
                </div>
              </div>
              
              {/* Company Details */}
              <div className="flex-1">
                <h1 className="text-xl font-bold text-black mb-1">{companyData.name}</h1>
                <p className="text-sm font-semibold text-black mb-1">{companyData.tagline}</p>
                <p className="text-xs text-black mb-1">{companyData.address}</p>
                <div className="text-xs text-black">
                  <span className="mr-4">{companyData.phone}</span>
                  <span>{companyData.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Title */}
          <div className="text-center py-3 border-b-2 border-gray-800 print:border-b-2 print:border-black">
            <h2 className="text-lg font-bold text-black">Purchase Order Report</h2>
          </div>

          {/* Loading/Error States */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
                <p className="text-sm">Loading purchase order data...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-8 print:hidden">
              <div className="text-center text-red-600">
                <p className="text-sm mb-2">Error loading data</p>
                <p className="text-xs mb-2">{error}</p>
                <button 
                  onClick={handleRefresh}
                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Purchase Orders Table */}
          {!loading && (
            <div className="p-2">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-800 print:border-b-2 print:border-black">
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">S.No</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Pur Req No</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Qtn.No</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Pur Order No</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Date</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Supplier</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Due Date</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-left">Store</th>
                    <th className="border-r border-gray-800 print:border-r print:border-black px-2 py-2 text-xs font-bold text-right">Amount</th>
                    <th className="px-2 py-2 text-xs font-bold text-center">Status</th>
                    {!isPreviewMode && <th className="print:hidden px-2 py-2 text-xs font-bold text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={isPreviewMode ? "10" : "11"} className="text-center py-8 text-gray-500">
                        No purchase orders found
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => (
                      <tr key={order.id} className="border-b border-gray-400 print:border-b print:border-black">
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2 text-xs">{order.sno}</td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.purReqNo}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.purReqNo}
                              onChange={(e) => updateOrder(order.id, 'purReqNo', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.qtnNo}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.qtnNo}
                              onChange={(e) => updateOrder(order.id, 'qtnNo', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs font-medium">{order.purOrderNo}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.purOrderNo}
                              onChange={(e) => updateOrder(order.id, 'purOrderNo', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.date}</span>
                          ) : (
                            <input
                              type="date"
                              value={order.date ? new Date(order.date.split('/').reverse().join('-')).toISOString().split('T')[0] : ''}
                              onChange={(e) => updateOrder(order.id, 'date', new Date(e.target.value).toLocaleDateString('en-GB'))}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.supplier}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.supplier}
                              onChange={(e) => updateOrder(order.id, 'supplier', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.dueDate}</span>
                          ) : (
                            <input
                              type="date"
                              value={order.dueDate ? new Date(order.dueDate.split('/').reverse().join('-')).toISOString().split('T')[0] : ''}
                              onChange={(e) => updateOrder(order.id, 'dueDate', new Date(e.target.value).toLocaleDateString('en-GB'))}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.store}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.store}
                              onChange={(e) => updateOrder(order.id, 'store', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            />
                          )}
                        </td>
                        <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2 text-right">
                          {isPreviewMode ? (
                            <span className="text-xs font-bold">{order.amount}</span>
                          ) : (
                            <input
                              type="text"
                              value={order.amount}
                              onChange={(e) => updateOrder(order.id, 'amount', e.target.value)}
                              className="w-full text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none text-right"
                            />
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {isPreviewMode ? (
                            <span className="text-xs">{order.status}</span>
                          ) : (
                            <select
                              value={order.status}
                              onChange={(e) => updateOrder(order.id, 'status', e.target.value)}
                              className="text-xs p-1 border border-gray-300 focus:border-blue-500 outline-none"
                            >
                              <option value="Pending">Pending</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          )}
                        </td>
                        {!isPreviewMode && (
                          <td className="px-2 py-2 text-center print:hidden">
                            <button
                              onClick={() => removeRow(order.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                  
                  {/* Empty rows for printing - only show if we have data */}
                  {orders.length > 0 && [...Array(Math.max(0, 15 - orders.length))].map((_, index) => (
                    <tr key={`empty-${index}`} className="border-b border-gray-400 print:border-b print:border-black h-8">
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="border-r border-gray-400 print:border-r print:border-black px-2 py-2"></td>
                      <td className="px-2 py-2"></td>
                      {!isPreviewMode && <td className="px-2 py-2 print:hidden"></td>}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total Section - only show if we have data */}
              {orders.length > 0 && (
                <div className="mt-4 flex justify-end border-t-2 border-gray-800 print:border-t-2 print:border-black pt-2">
                  <div className="text-right">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">Total:</span>
                      <span className="text-lg font-bold border-2 border-gray-800 print:border-2 print:border-black px-3 py-1">{calculateTotal()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-gray-800 print:border-t-2 print:border-black p-2 text-center">
            <p className="text-xs text-black">
              Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
            </p>
            <p className="text-xs text-black mt-1">Page 1 of 1</p>
          </div>
        </div>
      </div>

      {/* Debug Panel - Hidden in print */}
      <div className="print:hidden mt-8 p-4 bg-gray-100 rounded no-print max-w-4xl mx-auto">
        <h3 className="font-bold mb-2">API Debug Info:</h3>
        <div className="grid grid-cols-2 gap-2 text-sm mb-4">
          {Object.entries(params).map(([key, value]) => (
            <div key={key}>
              <strong>{key}:</strong> {value || 'Not provided'}
            </div>
          ))}
        </div>
        
        <div className="text-sm">
          <p><strong>Status:</strong> {loading ? 'Loading...' : error ? 'Error' : 'Success'}</p>
          <p><strong>Records:</strong> {orders.length}</p>
          {error && <p><strong>Error:</strong> {error}</p>}
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 0.5in;
            size: A4;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};

export default PurchaseOrderPrint;